import { FC, useContext, useEffect, useMemo, useRef } from 'react';
import {
  AlllieListStyled,
  ItemForm,
  RequiredAsterisk,
  StopTypeFilterLabel,
  StyledStopTypeContainer,
  TextAreaNoMargin,
} from '../AssignReasonForm.styles';
import { AllieItem, AllieSearchableSelect, AllieText } from '../../../../components';
import { AssignReasonFormContext } from '../AssignReasonForm.context';
import {
  FailureModeOption,
  NOT_APPLICABLE_FAILURE_MODE,
  RequiredFieldsConfig,
  Step2Props,
} from '../AssignReasonForm.types';
import { StopTypes } from '../AssignReasons.enum';
import { StopTypeChipsRow } from '../StopTypeChipsRow';
import { normalizeMachineId } from '../AssignReasonForm.utils';

const withEmptyOption = (
  options: { text: string; value: string | number }[],
  isRequired: boolean,
  emptyText: string
) => (isRequired ? options : [{ text: emptyText, value: 0 }, ...options]);

const isAvailableForMachine = (
  option: { machineIds?: number[]; preserveForEdit?: boolean },
  machineId?: number
) =>
  option.preserveForEdit ||
  machineId === undefined ||
  !option.machineIds?.length ||
  option.machineIds.includes(machineId);

type Step2Labels = Step2Props['labels'] & {
  stopTypeAll: string;
  stopTypePlanned: string;
  stopTypeUnplanned: string;
  stopTypeFilterSubtitle: string;
};

export const Step2: FC<
  Omit<Step2Props, 'labels'> & {
    labels: Step2Labels;
    setMachineSelected?: (value: number | undefined, isEdit?: boolean) => void;
    handleCategorySelection?: (value?: number, stopType?: StopTypes) => void;
    requiredFieldsConfig?: RequiredFieldsConfig;
  }
> = ({
  labels,
  groupsMotive,
  machines,
  failureModesByMotive,
  failureModesLoading,
  preserveNoMachineSelection,
  setMachineSelected,
  handleCategorySelection,
  trackingEvents,
  requiredFieldsConfig,
}) => {
  const { stepData, setStepData } = useContext(AssignReasonFormContext);

  const isGroupFirst = requiredFieldsConfig?.groupFirstEnabled === true;
  const isMachineRequired = !!(
    requiredFieldsConfig?.machineRequired || stepData.step2.isMachineRelated
  );

  const fieldLabels = useMemo(() => {
    const cfg = requiredFieldsConfig;
    const suffix = cfg && labels.optional ? ` (${labels.optional})` : '';

    const buildLabel = (label: string, isRequired: boolean) => ({
      text: isRequired ? label : `${label}${suffix}`,
      required: isRequired,
    });

    return {
      machine: buildLabel(labels.machine, isMachineRequired),
      component: buildLabel(labels.component, !!cfg?.componentRequired),
      failureMode: buildLabel(labels.failureMode, !!cfg?.failureModeRequired),
      note: buildLabel(labels.note, !!cfg?.commentRequired),
    };
  }, [requiredFieldsConfig, isMachineRequired, labels]);

  const currentStopType = (stepData.step1?.typeStop ?? StopTypes.all) as StopTypes;

  const filteredGroups = useMemo(() => {
    if (!groupsMotive) return [];
    if (currentStopType === StopTypes.all) {
      const planned = (groupsMotive[StopTypes.planned] ?? []).map((g) => ({
        ...g,
        _sourceStopType: StopTypes.planned,
      }));
      const unplanned = (groupsMotive[StopTypes.unplanned] ?? []).map((g) => ({
        ...g,
        _sourceStopType: StopTypes.unplanned,
      }));
      return [...unplanned, ...planned];
    }
    return (groupsMotive[currentStopType] ?? []).map((g) => ({
      ...g,
      _sourceStopType: currentStopType,
    }));
  }, [groupsMotive, currentStopType]);

  const currentGroupMotive = useMemo(
    () => filteredGroups.find((group) => group.value === stepData.step2.groupMotive),
    [filteredGroups, stepData.step2.groupMotive]
  );
  const lastSelectedGroupRef = useRef<(typeof filteredGroups)[number]>();

  useEffect(() => {
    if (currentGroupMotive) {
      lastSelectedGroupRef.current = currentGroupMotive;
    }
  }, [currentGroupMotive]);

  const groupMotiveSelected =
    currentGroupMotive ??
    (failureModesLoading &&
    lastSelectedGroupRef.current?.value === stepData.step2.groupMotive
      ? lastSelectedGroupRef.current
      : undefined);

  const motives = failureModesLoading ? [] : currentGroupMotive?.motives ?? [];

  useEffect(() => {
    if (
      failureModesLoading ||
      stepData.step2.groupMotive === undefined ||
      currentGroupMotive
    ) {
      return;
    }

    lastSelectedGroupRef.current = undefined;
    setStepData((current) => ({
      ...current,
      step2: {
        ...current.step2,
        groupMotive: undefined,
        isMachineRelated: undefined,
        motive: undefined,
        failureMode: undefined,
      },
    }));
  }, [
    currentGroupMotive,
    failureModesLoading,
    setStepData,
    stepData.step2.groupMotive,
  ]);

  const machineOptions = useMemo(() => {
    const options = machines ?? [];
    return isMachineRequired && !preserveNoMachineSelection
      ? options.filter((machine) => machine.value !== undefined)
      : options;
  }, [isMachineRequired, machines, preserveNoMachineSelection]);

  const components = useMemo(() => {
    const selectedMachine = machines?.find((m) => m.value === stepData.step2.machine);
    if (!selectedMachine) return [];
    return withEmptyOption(
      selectedMachine.components,
      !!requiredFieldsConfig?.componentRequired,
      labels.empty
    );
  }, [machines, stepData.step2.machine, labels.empty, requiredFieldsConfig?.componentRequired]);

  const failureModeOptions = useMemo(() => {
    if (!stepData.step2.motive) return [];
    const modes = (failureModesByMotive?.[stepData.step2.motive] ?? []).filter((mode) =>
      isAvailableForMachine(mode, stepData.step2.machine)
    );
    if (modes.length === 0) {
      return [{ text: labels.notApplicable, value: NOT_APPLICABLE_FAILURE_MODE }];
    }
    return withEmptyOption(modes, !!requiredFieldsConfig?.failureModeRequired, labels.empty);
  }, [
    stepData.step2.motive,
    stepData.step2.machine,
    failureModesByMotive,
    requiredFieldsConfig?.failureModeRequired,
    labels.empty,
    labels.notApplicable,
  ]);

  useEffect(() => {
    if (failureModesLoading) return;

    const selectedFailureMode = stepData.step2.failureMode;
    if (
      selectedFailureMode !== undefined &&
      !failureModeOptions.some((mode) => mode.value === selectedFailureMode)
    ) {
      setStepData({
        ...stepData,
        step2: { ...stepData.step2, failureMode: undefined },
      });
    }
  }, [failureModeOptions, failureModesLoading, setStepData, stepData]);

  useEffect(() => {
    stepData.step2.machine && setMachineSelected?.(stepData.step2.machine, true);
  }, []);

  const handleTypeChange = (type: StopTypes) => {
    handleCategorySelection?.(undefined, undefined);
    setMachineSelected?.(undefined);
    setStepData({
      ...stepData,
      step1: { ...stepData.step1, typeStop: type },
      step2: {
        ...stepData.step2,
        groupMotive: undefined,
        motive: undefined,
        failureMode: undefined,
        isMachineRelated: undefined,
        resolvedStopType: type !== StopTypes.all ? type : undefined,
      },
    });
  };

  const machineSelect = (
    <AllieItem data-testid="machine-select">
      <ItemForm>
        <AllieText color="neutral-50">
          {fieldLabels.machine.text}
          {fieldLabels.machine.required && <RequiredAsterisk>{'*'}</RequiredAsterisk>}
        </AllieText>
        <AllieSearchableSelect
          options={machineOptions}
          selectedValue={
            machineOptions.find((machine) => machine.value === stepData.step2.machine)?.text
          }
          disabled={machineOptions.length === 0}
          trackSelectionEvent={trackingEvents?.step2DropdownTrackingEvent}
          field={'Maquina'}
          handleOnSelect={(value) => {
            const machineId = normalizeMachineId(value);
            setMachineSelected?.(machineId);
            setStepData({
              ...stepData,
              step2: {
                ...stepData.step2,
                machine: machineId,
                component: undefined,
                motive: undefined,
                failureMode: undefined,
              },
            });
          }}
        />
      </ItemForm>
    </AllieItem>
  );

  const componentSelect = !!stepData.step2.machine && (
    <AllieItem>
      <ItemForm>
        <AllieText color="neutral-50">
          {fieldLabels.component.text}
          {fieldLabels.component.required && <RequiredAsterisk>*</RequiredAsterisk>}
        </AllieText>
        <AllieSearchableSelect
          options={components}
          trackSelectionEvent={trackingEvents?.step2DropdownTrackingEvent}
          field={'Componente'}
          selectedValue={
            components.find((component) => component.value === stepData.step2.component)?.text
          }
          handleOnSelect={(value) => {
            setStepData({
              ...stepData,
              step2: {
                ...stepData.step2,
                component: value === 0 ? undefined : value,
              },
            });
          }}
        />
      </ItemForm>
    </AllieItem>
  );

  const groupSelect = (
    <AllieItem>
      <ItemForm>
        <AllieText color="neutral-50">
          {labels.groupMotive}
          <RequiredAsterisk>*</RequiredAsterisk>
        </AllieText>
        <AllieSearchableSelect
          data-testid="group-motive-select"
          options={filteredGroups}
          trackSelectionEvent={trackingEvents?.step2DropdownTrackingEvent}
          field={'Grupo'}
          selectedValue={groupMotiveSelected?.text}
          handleOnSelect={(value) => {
            const group = filteredGroups.find((g) => g.value === value);
            const resolvedStopType = group?._sourceStopType ?? currentStopType;
            if (isGroupFirst) {
              handleCategorySelection?.(Number(value), resolvedStopType);
            }
            setStepData({
              ...stepData,
              step2: {
                ...stepData.step2,
                groupMotive: value,
                motive: undefined,
                failureMode: undefined,
                isMachineRelated: group?.isMachineRelated,
                resolvedStopType,
                ...(isGroupFirst && { machine: undefined, component: undefined }),
              },
            });
          }}
        />
      </ItemForm>
    </AllieItem>
  );

  const motiveSelect = (
    <AllieItem>
      <ItemForm>
        <AllieText color="neutral-50">
          {labels.motive}
          <RequiredAsterisk>*</RequiredAsterisk>
        </AllieText>
        <AllieSearchableSelect
          data-testid="motive-select"
          options={motives}
          trackSelectionEvent={trackingEvents?.step2DropdownTrackingEvent}
          field={'Motivo'}
          disabled={motives.length === 0}
          selectedValue={motives.find((motive) => motive.value === stepData.step2.motive)?.text}
          handleOnSelect={(value) => {
            const motiveHasFailureModes =
              (failureModesByMotive?.[value as number] ?? []).length > 0;
            setStepData({
              ...stepData,
              step2: {
                ...stepData.step2,
                motive: value,
                failureMode: motiveHasFailureModes ? undefined : NOT_APPLICABLE_FAILURE_MODE,
              },
            });
          }}
        />
      </ItemForm>
    </AllieItem>
  );

  const failureModeSelect = !!stepData.step2.motive && (
    <AllieItem>
      <ItemForm>
        <AllieText color="neutral-50">
          {fieldLabels.failureMode.text}
          {fieldLabels.failureMode.required && <RequiredAsterisk>*</RequiredAsterisk>}
        </AllieText>
        <AllieSearchableSelect
          data-testid="failure-mode-select"
          options={failureModeOptions}
          trackSelectionEvent={trackingEvents?.step2DropdownTrackingEvent}
          field={'ModoFalla'}
          disabled={
            failureModeOptions.length === 0 ||
            (failureModeOptions.length === 1 &&
              failureModeOptions[0].value === NOT_APPLICABLE_FAILURE_MODE)
          }
          selectedValue={
            failureModeOptions.find((fm) => fm.value === stepData.step2.failureMode)?.text
          }
          handleOnSelect={(value) => {
            const selectedMode = failureModeOptions.find(
              (mode) => mode.value === value
            ) as FailureModeOption | undefined;
            if (
              selectedMode &&
              value !== 0 &&
              value !== NOT_APPLICABLE_FAILURE_MODE &&
              stepData.step2.motive
            ) {
              trackingEvents?.failureModeSelected?.({
                failureModeId: Number(value),
                stopReasonId: stepData.step2.motive,
                machineId: stepData.step2.machine,
                isRestrictedMode:
                  selectedMode.isRestricted ?? Boolean(selectedMode.machineIds?.length),
                totalOptionsShown: failureModeOptions.filter(
                  (mode) => mode.value !== 0 && mode.value !== NOT_APPLICABLE_FAILURE_MODE
                ).length,
              });
            }
            setStepData({
              ...stepData,
              step2: {
                ...stepData.step2,
                failureMode: value === 0 ? undefined : value,
              },
            });
          }}
        />
      </ItemForm>
    </AllieItem>
  );

  const noteField = (
    <AllieItem>
      <ItemForm>
        <AllieText color="neutral-50">
          {fieldLabels.note.text}
          {fieldLabels.note.required && <RequiredAsterisk>*</RequiredAsterisk>}
        </AllieText>
        <TextAreaNoMargin
          corner="rounded-edges-plus"
          value={stepData.step2.note}
          onChange={(e: CustomEvent<{ value?: string | null }>) => {
            setStepData({ ...stepData, step2: { ...stepData.step2, note: e.detail.value } });
          }}
        />
      </ItemForm>
    </AllieItem>
  );

  return (
    <>
      <StyledStopTypeContainer>
        {labels.stopTypeFilterSubtitle && (
          <StopTypeFilterLabel>{labels.stopTypeFilterSubtitle}</StopTypeFilterLabel>
        )}
        <StopTypeChipsRow
          selectedType={currentStopType}
          onTypeChange={handleTypeChange}
          labels={{
            all: labels.stopTypeAll,
            planned: labels.stopTypePlanned,
            unplanned: labels.stopTypeUnplanned,
          }}
        />
      </StyledStopTypeContainer>
      <AlllieListStyled>
        {isGroupFirst ? (
          <>
            {groupSelect}
            {stepData.step2.isMachineRelated && machineSelect}
            {stepData.step2.isMachineRelated && componentSelect}
            {motiveSelect}
            {failureModeSelect}
            {noteField}
          </>
        ) : (
          <>
            {machineSelect}
            {componentSelect}
            {groupSelect}
            {motiveSelect}
            {failureModeSelect}
            {noteField}
          </>
        )}
      </AlllieListStyled>
    </>
  );
};
